import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Loader2, BookMarked, Repeat, StickyNote, Calendar as CalendarIcon } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { fmtDateOnly } from "@/lib/datetime";

const toISO = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const parseISO = (s) => {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
};
const todayISO = () => toISO(new Date());

// Most recent Monday/Thursday on or before today (fallback when no semester yet).
function recentClassDayISO() {
  const d = new Date();
  for (let i = 0; i < 7; i++) {
    const dow = d.getDay();
    if (dow === 1 || dow === 4) return toISO(d);
    d.setDate(d.getDate() - 1);
  }
  return toISO(new Date());
}

// Most recent selectable Mon/Thu: on or before min(today, semester end) and within the semester.
function defaultClassDay(sem) {
  const today = new Date();
  let hi = today;
  if (sem?.end_date) {
    const end = parseISO(sem.end_date);
    if (end < today) hi = end;
  }
  const d = new Date(hi);
  for (let i = 0; i < 400; i++) {
    const dow = d.getDay();
    const iso = toISO(d);
    if ((dow === 1 || dow === 4) && (!sem?.start_date || iso >= sem.start_date)) return iso;
    d.setDate(d.getDate() - 1);
  }
  return null;
}

const BLANK = { memorization: "", review: "", other_notes: "", points: 0 };

function fromRecord(r) {
  return {
    memorization: r.memorization || "",
    review: r.review || "",
    other_notes: r.other_notes || "",
    points: r.points ?? 0,
  };
}

export default function RecordFormDialog({ open, onOpenChange, studentId, record, onSaved, admin = false, teachers = [], defaultTeacherId = "" }) {
  const editMode = !!record;
  const [date, setDate] = useState(recentClassDayISO());
  const [recordId, setRecordId] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [teacherCredit, setTeacherCredit] = useState("");
  const [semester, setSemester] = useState(null);
  const [calOpen, setCalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!open) return;
    if (editMode) {
      setDate(record.date);
      setRecordId(record.id);
      setForm(fromRecord(record));
      setTeacherCredit(record.teacher_id || defaultTeacherId || "");
    } else {
      setDate(recentClassDayISO());
      setRecordId(null);
      setForm(BLANK);
      setTeacherCredit(defaultTeacherId || "");
      api.get("/semester/active").then((r) => {
        setSemester(r.data);
        const dflt = defaultClassDay(r.data);
        if (dflt) setDate(dflt);
      }).catch(() => {});
    }
  }, [open, editMode, record, defaultTeacherId]);

  // In create mode (teacher only), look up an existing record whenever the date changes.
  const lookupForDate = useCallback(
    async (d) => {
      if (editMode || !open || admin) return;
      setLoading(true);
      try {
        const { data } = await api.get("/teacher/records", {
          params: { student_id: studentId, date: d },
        });
        if (data) {
          setRecordId(data.id);
          setForm(fromRecord(data));
        } else {
          setRecordId(null);
          setForm(BLANK);
        }
      } catch (e) {
        /* ignore */
      } finally {
        setLoading(false);
      }
    },
    [editMode, open, studentId]
  );

  useEffect(() => {
    if (open && !editMode) lookupForDate(date);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, open, editMode]);

  // A day is selectable only if it's Mon/Thu, not in the future, and within the active semester.
  const isDayDisabled = (day) => {
    const dow = day.getDay(); // Mon=1, Thu=4
    if (dow !== 1 && dow !== 4) return true;
    const iso = toISO(day);
    if (iso > todayISO()) return true;
    if (semester && (iso < semester.start_date || iso > semester.end_date)) return true;
    return false;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (saving) return; // guard against a double-tap firing two requests
    if (admin && !teacherCredit) {
      toast.error("Select which teacher to credit");
      return;
    }
    setSaving(true);
    const payload = {
      memorization: form.memorization,
      review: form.review,
      other_notes: form.other_notes,
      points: Number(form.points) || 0,
    };
    if (admin) payload.teacher_id = teacherCredit;
    const recPath = admin ? "/admin/records" : "/teacher/records";
    try {
      if (recordId) {
        await api.put(`${recPath}/${recordId}`, payload);
      } else {
        try {
          await api.post(recPath, { student_id: studentId, date, ...payload });
        } catch (err) {
          const detail = err.response?.data?.detail;
          const existingId =
            detail && typeof detail === "object" ? detail.existing_record_id : null;
          if (err.response?.status === 409 && existingId) {
            // Record already exists (e.g. a double-tap) — silently save as an edit.
            await api.put(`${recPath}/${existingId}`, payload);
            setRecordId(existingId);
          } else {
            throw err;
          }
        }
      }
      toast.success("Record saved");
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">
            {recordId ? "Edit daily record" : "New daily record"}
          </DialogTitle>
          <DialogDescription>
            Memorization, review, notes and points for the selected class day.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-5" data-testid="record-form">
          {/* Date */}
          <div className="space-y-2">
            <Label>Date</Label>
            {editMode ? (
              <Input className="h-11" value={fmtDateOnly(date)} disabled data-testid="record-date-input" />
            ) : (
              <Popover open={calOpen} onOpenChange={setCalOpen}>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    data-testid="record-date-input"
                    className="h-11 w-full justify-start font-normal"
                  >
                    <CalendarIcon className="h-4 w-4 mr-2 text-muted-foreground" />
                    {date ? fmtDateOnly(date) : "Select a class day"}
                    {loading && <Loader2 className="h-4 w-4 animate-spin ml-auto" />}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={date ? parseISO(date) : undefined}
                    defaultMonth={date ? parseISO(date) : new Date()}
                    onSelect={(d) => {
                      if (d) {
                        setDate(toISO(d));
                        setCalOpen(false);
                      }
                    }}
                    disabled={isDayDisabled}
                  />
                </PopoverContent>
              </Popover>
            )}
            {!editMode && (
              <p className="text-xs text-muted-foreground">
                Only Monday &amp; Thursday class days within the active semester can be selected.
              </p>
            )}
            {recordId && !editMode && (
              <p className="text-xs text-[hsl(var(--gold))]" data-testid="record-exists-hint">
                A record already exists for this date — you're editing it.
              </p>
            )}
          </div>

          {/* Credited teacher (admin only) */}
          {admin && (
            <div className="space-y-2">
              <Label>Credited teacher</Label>
              <Select value={teacherCredit} onValueChange={setTeacherCredit}>
                <SelectTrigger className="h-11" data-testid="record-teacher-credit">
                  <SelectValue placeholder="Select teacher to credit" />
                </SelectTrigger>
                <SelectContent>
                  {teachers.map((t) => (
                    <SelectItem key={t.id} value={t.id} data-testid={`credit-teacher-${t.id}`}>
                      {t.full_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Memorization */}
          <div className="space-y-2">
            <Label htmlFor="memorization" className="flex items-center gap-2">
              <BookMarked className="h-4 w-4 text-primary" /> Memorization
            </Label>
            <Textarea
              id="memorization"
              data-testid="record-memorization"
              rows={2}
              placeholder="e.g. An-Naba 1–15, fluent"
              value={form.memorization}
              onChange={(e) => set("memorization", e.target.value)}
            />
          </div>

          {/* Review */}
          <div className="space-y-2">
            <Label htmlFor="review" className="flex items-center gap-2">
              <Repeat className="h-4 w-4 text-primary" /> Review
            </Label>
            <Textarea
              id="review"
              data-testid="record-review"
              rows={2}
              placeholder="e.g. Al-Mulk 1–10, needs tajweed work"
              value={form.review}
              onChange={(e) => set("review", e.target.value)}
            />
          </div>

          {/* Other notes */}
          <div className="space-y-2">
            <Label htmlFor="other-notes" className="flex items-center gap-2">
              <StickyNote className="h-4 w-4 text-primary" /> Other notes
            </Label>
            <Textarea
              id="other-notes"
              data-testid="record-other-notes"
              rows={2}
              placeholder="Reading progress, behaviour, anything else"
              value={form.other_notes}
              onChange={(e) => set("other_notes", e.target.value)}
            />
          </div>

          {/* Points */}
          <div className="space-y-2">
            <Label htmlFor="points">Daily points (0–20)</Label>
            <Input
              id="points"
              data-testid="record-points"
              type="number"
              min={0}
              max={20}
              className="h-11 w-32"
              value={form.points}
              onChange={(e) => set("points", e.target.value)}
            />
          </div>

          <DialogFooter>
            <Button type="submit" disabled={saving} className="h-11" data-testid="record-save-button">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : recordId ? "Save changes" : "Save record"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
